import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Moon, Sun, Menu, X, LogOut } from "lucide-react";
import Logo from "@/components/Logo";
import Avatar from "@/components/Avatar";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { cn } from "@/lib/format";

function UnreadBadge({ count, className = "" }) {
  if (!count) return null;
  return (
    <span data-testid="inbox-unread-badge"
      className={cn("inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-600 px-1.5 text-[11px] font-bold text-white", className)}>
      {count > 9 ? "9+" : count}
    </span>
  );
}

function ThemeToggle() {
  const { dark, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-foreground transition-colors hover:bg-muted"
      aria-label="Toggle theme"
    >
      {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  );
}

export default function AppShell({ tabs = [], children, fullBleed = false, accountTo = "/account" }) {
  const { user, signOut } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Poll unread message count so users see new messages without opening the inbox.
  React.useEffect(() => {
    if (!user) return undefined;
    let stop = false;
    const fetchUnread = () =>
      api("/inbox/unread").then((r) => { if (!stop) setUnread(r?.total || 0); }).catch(() => {});
    fetchUnread();
    const iv = setInterval(fetchUnread, 15000);
    const onFocus = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    return () => { stop = true; clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [user]);

  // Keep the app sized to the visible viewport so the mobile keyboard doesn't push the layout.
  React.useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    const apply = () => root.style.setProperty("--app-height", `${vv ? vv.height : window.innerHeight}px`);
    apply();
    vv?.addEventListener("resize", apply);
    vv?.addEventListener("scroll", apply);
    window.addEventListener("resize", apply);
    return () => {
      vv?.removeEventListener("resize", apply);
      vv?.removeEventListener("scroll", apply);
      window.removeEventListener("resize", apply);
    };
  }, []);

  const logout = () => { setOpen(false); signOut(); navigate("/auth", { replace: true }); };

  return (
    <div style={{ height: "var(--app-height, 100dvh)" }} className="flex flex-col overflow-hidden bg-background">
      {/* Desktop / top header */}
      <header className="z-30 hidden shrink-0 items-center justify-between border-b border-border bg-card px-6 py-3 md:flex">
        <div className="flex items-center gap-8">
          <button onClick={() => navigate("/")} className="flex items-center">
            <Logo size={34} />
          </button>
          <nav className="flex items-center gap-1">
            {tabs.map((t) => (
              t.disabled ? (
                <span
                  key={t.to}
                  data-testid={`nav-${t.label.toLowerCase()}`}
                  title="Available once your account is approved"
                  aria-disabled="true"
                  className="flex cursor-not-allowed items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground/40"
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </span>
              ) : (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`nav-${t.label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors",
                    isActive
                      ? "bg-accent text-brand-700 dark:text-brand-400"
                      : "text-muted-foreground hover:text-foreground"
                  )
                }
              >
                <t.icon className="h-4 w-4" />
                {t.label}
                {t.label === "Inbox" && <UnreadBadge count={unread} className="ml-1" />}
              </NavLink>
              )
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button onClick={() => navigate(accountTo)} data-testid="header-avatar">
            <Avatar src={user?.photo} name={user?.name} size={38} />
          </button>
        </div>
      </header>

      {/* Mobile top bar with hamburger */}
      <header className="z-30 flex shrink-0 items-center justify-between border-b border-border bg-card px-3 py-2.5 md:hidden">
        <button onClick={() => setOpen(true)} data-testid="menu-open" aria-label="Open menu"
          className="relative flex h-10 w-10 items-center justify-center rounded-full text-foreground transition-colors hover:bg-secondary">
          <Menu className="h-6 w-6" />
          {unread > 0 && <span data-testid="menu-unread-dot" className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-brand-600 ring-2 ring-card" />}
        </button>
        <Logo size={30} />
        <button onClick={() => navigate(accountTo)} data-testid="mobile-avatar" aria-label="Account">
          <Avatar src={user?.photo} name={user?.name} size={34} />
        </button>
      </header>

      <main className={cn("relative flex-1 overflow-hidden", fullBleed ? "" : "overflow-y-auto")}>
        {children}
      </main>

      {/* Mobile slide-in sidebar */}
      <div className={cn("fixed inset-0 z-50 md:hidden", open ? "" : "pointer-events-none")} data-testid="mobile-sidebar">
        <div
          onClick={() => setOpen(false)}
          className={cn("absolute inset-0 bg-black/50 transition-opacity duration-300", open ? "opacity-100" : "opacity-0")}
        />
        <aside
          className={cn(
            "absolute inset-y-0 left-0 flex w-72 max-w-[82%] flex-col bg-card shadow-2xl transition-transform duration-300",
            open ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <Logo size={30} />
            <button onClick={() => setOpen(false)} data-testid="menu-close" aria-label="Close menu"
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary">
              <X className="h-5 w-5" />
            </button>
          </div>

          <button onClick={() => { setOpen(false); navigate(accountTo); }} data-testid="sidebar-account"
            className="flex items-center gap-3 border-b border-border px-4 py-4 text-left transition-colors hover:bg-secondary">
            <Avatar src={user?.photo} name={user?.name} size={44} />
            <div className="min-w-0">
              <p className="truncate font-bold text-foreground">{user?.name || "Account"}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </button>

          <nav className="flex-1 space-y-1 overflow-y-auto p-3">
            {tabs.map((t) => (
              t.disabled ? (
                <div
                  key={t.to}
                  data-testid={`tab-${t.label.toLowerCase()}`}
                  aria-disabled="true"
                  className="flex cursor-not-allowed items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-muted-foreground/40"
                >
                  <t.icon className="h-5 w-5" />
                  {t.label}
                  <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-muted-foreground/50">Locked</span>
                </div>
              ) : (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                onClick={() => setOpen(false)}
                data-testid={`tab-${t.label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold transition-colors",
                    isActive ? "bg-accent text-brand-700 dark:text-brand-400" : "text-muted-foreground hover:bg-secondary"
                  )
                }
              >
                <t.icon className="h-5 w-5" />
                {t.label}
                {t.label === "Inbox" && <UnreadBadge count={unread} className="ml-auto" />}
              </NavLink>
              )
            ))}
          </nav>

          <div className="space-y-1 border-t border-border p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
            <button onClick={toggle} data-testid="sidebar-theme"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-muted-foreground hover:bg-secondary">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />} {dark ? "Light mode" : "Dark mode"}
            </button>
            <button onClick={logout} data-testid="sidebar-signout"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-destructive hover:bg-secondary">
              <LogOut className="h-5 w-5" /> Sign out
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
